import { Component } from '@theme/component';
import { Slideshow } from './slideshow.js';
import { ProductCard } from './product-card.js';
import { SwatchUpdateEvent  } from '@theme/events';

/**
 * A Picker Swatcher element
 * @typedef {Object} Refs
 * @property {HTMLElement[]} listSwatchItem - The list of swatch item
 *
 * @extends {Component<Refs>}
 */

class PickerSwatcher extends Component {
  requiredRefs = ['listSwatchItem'];

  /** @type {ProductCard | null} */
  #productCard = this.closest('product-card');

  /** @type {Slideshow | null} */
  #productSlideshow = this.#productCard?.querySelector('slideshow-component') || null;

  /** @type {HTMLElement | null} */
  #productImageContainer = this.#productCard?.querySelector('.product-image__container') || null;

  /** @type {HTMLElement | null} */
  #tooltip = null;

  /** @type {Number | null} */
  #swatchHoverTimeout = null;

  /** @type {Map<string, {main: HTMLImageElement, hover: HTMLImageElement}>} */
  #preloadedImages = new Map();

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#tooltip = document.querySelector('.tooltip-swatch');
    if (!this.#tooltip) {
      PickerSwatcher.createTooltipElement();
      this.#tooltip = document.querySelector('.tooltip-swatch');
    }

    this.#setupIntersectionObserver();

    this.#preloadPriorityImages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    this.#preloadedImages.clear();
  }

  #setupIntersectionObserver() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.#preloadAllImages();
            this.#intersectionObserver?.disconnect();
          }
        });
      },
      { rootMargin: '50px' }
    );

    this.#intersectionObserver.observe(this);
  }

  #preloadPriorityImages() {
    const { listSwatchItem } = this.refs;
    const priorityCount = Math.min(3, listSwatchItem.length);

    for (let i = 0; i < priorityCount; i++) {
      const item = listSwatchItem[i];
      if (item instanceof HTMLElement) {
        this.#preloadSwatchImages(item);
      }
    }
  }

  #preloadAllImages() {
    const { listSwatchItem } = this.refs;

    listSwatchItem.forEach((swatch, index) => {
      if (index >= 3) {
        setTimeout(() => {
          this.#preloadSwatchImages(swatch);
        }, index * 50);
      }
    });
  }

  /**
   * Preload images for a specific swatch
   * @param {HTMLElement} swatch
   */
  #preloadSwatchImages(swatch) {
    const {
      dataset: { id, srcMain, srcHover, srcsetMain, srcsetHover },
    } = swatch;

    if (!id || this.#preloadedImages.has(id)) return;

    const mainImg = new Image();
    const hoverImg = new Image();

    // Set srcset first for better resolution selection
    if (srcsetMain) mainImg.srcset = srcsetMain;
    if (srcMain) mainImg.src = srcMain;

    if (srcsetHover) hoverImg.srcset = srcsetHover;
    if (srcHover) hoverImg.src = srcHover;

    // Store preloaded images
    this.#preloadedImages.set(id, { main: mainImg, hover: hoverImg });
  }

  /**
   * @param {string} index
   */
  handlePointerEnter(index) {
    if (this.#swatchHoverTimeout) clearTimeout(this.#swatchHoverTimeout);

    const { listSwatchItem } = this.refs;
    const currentSwatch = listSwatchItem[Number(index)];

    // Preload this image immediately if not already loaded
    if (currentSwatch) {
      this.#preloadSwatchImages(currentSwatch);
    }

    this.#swatchHoverTimeout = setTimeout(() => {
      this.showActiveSwatchImage(index);
    }, 200);

    this.dispatchEvent(new SwatchUpdateEvent({ id: currentSwatch?.dataset.id || '', target: currentSwatch }));
  }

  handlePointerLeave() {
    if (this.#swatchHoverTimeout) clearTimeout(this.#swatchHoverTimeout);
    this.hideActiveSwatchImage();
  }

  hideActiveSwatchImage() {
    if (this.#tooltip) this.#tooltip.classList.remove('show');
  }

  /**
   * @param {string} index
   */
  showActiveSwatchImage(index) {
    const { listSwatchItem } = this.refs;
    const currentSwatch = listSwatchItem[Number(index)];
    listSwatchItem.forEach((swatchItem) => swatchItem.setAttribute('data-active', 'false'));
    if (currentSwatch) {
      this.#handleChangeImage(currentSwatch);
      this.#handleChangeImages(currentSwatch);
    }
  }

  /**
   * @param {HTMLElement} swatch
   */
  #handleChangeImages(swatch) {
    if (!this.#productImageContainer) return;

    const {
      dataset: { id, srcMain, srcHover, srcsetMain, srcsetHover },
    } = swatch;

    /**
     * @type {HTMLImageElement | null}
     */
    const mainImage = this.#productImageContainer.querySelector('.product-image__main') || null;
    /**
     * @type {HTMLImageElement | null}
     */
    const hoverImage = this.#productImageContainer.querySelector('.product-image__hover') || null;

    // Check if we have preloaded images
    const preloaded = this.#preloadedImages.get(id || '');

    if (mainImage) {
      // Use preloaded image if available for instant display
      if (preloaded?.main.complete) {
        mainImage.src = preloaded.main.src;
        if (srcsetMain) mainImage.srcset = srcsetMain;
        // No loading state needed - image is already loaded
        this.#productImageContainer?.classList.remove('loading');
      } else {
        // Fallback to normal loading
        if (srcMain) mainImage.src = srcMain;
        if (srcsetMain) mainImage.srcset = srcsetMain;
        this.#productImageContainer?.classList.add('loading');
        mainImage.addEventListener(
          'load',
          () => {
            this.#productImageContainer?.classList.remove('loading');
          },
          { once: true }
        );
      }
    }

    if (hoverImage) {
      // Use preloaded hover image if available
      if (preloaded?.hover.complete) {
        hoverImage.src = preloaded.hover.src;
        if (srcsetHover) hoverImage.srcset = srcsetHover;
      } else {
        if (srcHover) hoverImage.src = srcHover;
        if (srcsetHover) hoverImage.srcset = srcsetHover;
      }
    }
  }
  /**
   * @param {HTMLElement} swatch
   */
  async #handleChangeImage(swatch) {
    swatch.setAttribute('data-active', 'true');
    const {
      dataset: { id, slideId, tooltip },
    } = swatch;
    if (this.#productSlideshow?.select && slideId) {
      await this.#productSlideshow.select({ id: slideId }, undefined, { animate: true });
    }
    if (tooltip && swatch) {
      this.#undateTooltipElement(tooltip, swatch);
    }
  }
  /**
   * @param {string} text
   * @param {HTMLElement} swatch
   */
  #undateTooltipElement(text, swatch) {
    /** @type {HTMLElement | null} */
    const tooltip = this.#tooltip;
    /** @type {HTMLElement | null | undefined} */
    const tooltipText = tooltip?.querySelector('span');
    /** @type {HTMLElement | null | undefined} */
    const arrow = tooltip?.querySelector('.tooltip-swatch__arrow');

    if (!tooltipText || !tooltip || !arrow) return;

    tooltipText.textContent = '';
    tooltipText.textContent = text;

    tooltip.classList.add('show');

    const rect = swatch.getBoundingClientRect();
    const ttRect = tooltip.getBoundingClientRect();

    let x = rect.left + rect.width / 2 - ttRect.width / 2 + window.scrollX;
    let y = rect.top - rect.height / 2 + window.scrollY;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (x < window.scrollX + 5) {
      x = window.scrollX + 5;
    } else if (x + ttRect.width > window.scrollX + viewportWidth - 5) {
      x = window.scrollX + viewportWidth - ttRect.width - 5;
    }
    if (y < window.scrollY + 5) {
      y = window.scrollY + 5;
    } else if (y + ttRect.height > window.scrollY + viewportHeight - 5) {
      y = window.scrollY + viewportHeight - ttRect.height - 5;
    }

    tooltip.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0px)`;

    const swatchCenterX = rect.left + rect.width / 2 + window.scrollX;
    const tooltipLeft = x;

    let arrowX = swatchCenterX - tooltipLeft;

    const arrowMargin = 10;
    arrowX = Math.min(Math.max(arrowX, arrowMargin), ttRect.width - arrowMargin);

    arrow.style.left = `${arrowX}px`;
  }

  static createTooltipElement() {
    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip-swatch');

    const tooltipSpan = document.createElement('span');
    tooltip.appendChild(tooltipSpan);

    const arrow = document.createElement('div');
    arrow.classList.add('tooltip-swatch__arrow');

    tooltip.appendChild(arrow);
    document.body.appendChild(tooltip);
  }
}

if (!customElements.get('picker-swatcher')) {
  customElements.define('picker-swatcher', PickerSwatcher);
}
